import "./security.css";
import { DesktopIcon, SignOutIcon } from "@phosphor-icons/react";
import { useState } from "react";
import type { AccountSecurityManagement } from "../types";
import {
  authenticationActionError,
  relativeSessionActivity,
} from "./presentation";

export function ActiveBrowserSessionsCard({
  accountSecurity,
  nowUnixMs = Date.now(),
}: {
  accountSecurity: AccountSecurityManagement;
  nowUnixMs?: number;
}) {
  const [confirmOthers, setConfirmOthers] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackTone, setFeedbackTone] = useState<"error" | "success">(
    "success",
  );
  const [pendingSession, setPendingSession] = useState<
    string | "others" | null
  >(null);
  const sessions = accountSecurity.snapshot?.sessions ?? [];
  const otherSessionCount = sessions.filter(
    (session) => !session.current,
  ).length;

  async function revoke(sessionId: string, deviceLabel: string): Promise<void> {
    setPendingSession(sessionId);
    setFeedback("");
    try {
      await accountSecurity.revokeSession(sessionId);
      setFeedbackTone("success");
      setFeedback(`${deviceLabel} signed out.`);
    } catch (cause) {
      setFeedbackTone("error");
      setFeedback(
        authenticationActionError(
          cause,
          "That browser could not be signed out.",
        ),
      );
    } finally {
      setPendingSession(null);
    }
  }

  async function revokeOthers(): Promise<void> {
    setPendingSession("others");
    setFeedback("");
    try {
      await accountSecurity.revokeOtherSessions();
      setConfirmOthers(false);
      setFeedbackTone("success");
      setFeedback(
        "Other browser sessions are signed out. This browser stays connected.",
      );
    } catch (cause) {
      setFeedbackTone("error");
      setFeedback(
        authenticationActionError(
          cause,
          "Other browsers could not be signed out.",
        ),
      );
    } finally {
      setPendingSession(null);
    }
  }

  return (
    <section className="pm-passport-card pm-active-sessions-card">
      <header>
        <div>
          <h2>Active browser sessions</h2>
          <p>Review every browser that can currently enter your Account.</p>
        </div>
        <small>
          {accountSecurity.state === "ready"
            ? `${sessions.length} active`
            : "Authoritative server list"}
        </small>
      </header>

      {accountSecurity.state === "loading" ? (
        <p className="pm-security-state" role="status">
          Finding active browsers…
        </p>
      ) : accountSecurity.state === "unavailable" ||
        accountSecurity.snapshot === undefined ? (
        <div className="pm-security-state" role="alert">
          <strong>Active browsers could not be loaded.</strong>
          <span>
            No session identifiers or secrets were stored in this page.
          </span>
          <button onClick={() => void accountSecurity.refresh()} type="button">
            Try again
          </button>
        </div>
      ) : (
        <div className="pm-session-list">
          {sessions.map((session) => (
            <div className="pm-browser-session" key={session.id}>
              <span className="pm-auth-method-icon">
                <DesktopIcon size={21} weight="duotone" />
              </span>
              <div>
                <strong>{session.deviceLabel}</strong>
                <p>
                  Active{" "}
                  {relativeSessionActivity(
                    session.lastActiveAtUnixMs,
                    nowUnixMs,
                  )}
                </p>
              </div>
              {session.current ? (
                <small>This browser</small>
              ) : (
                <button
                  aria-label={`Sign out ${session.deviceLabel}, active ${relativeSessionActivity(
                    session.lastActiveAtUnixMs,
                    nowUnixMs,
                  )}`}
                  disabled={pendingSession !== null}
                  onClick={() => void revoke(session.id, session.deviceLabel)}
                  type="button"
                >
                  <SignOutIcon aria-hidden="true" size={17} />
                  {pendingSession === session.id ? "Signing out…" : "Sign out"}
                </button>
              )}
            </div>
          ))}

          {otherSessionCount > 0 ? (
            confirmOthers ? (
              <div
                className="pm-security-confirmation"
                role="group"
                aria-label="Confirm other browser sign out"
              >
                <div>
                  <strong>Keep only this browser?</strong>
                  <p>
                    {otherSessionCount} other{" "}
                    {otherSessionCount === 1 ? "session" : "sessions"} will end
                    immediately.
                  </p>
                </div>
                <button onClick={() => setConfirmOthers(false)} type="button">
                  Cancel
                </button>
                <button
                  className="danger"
                  disabled={pendingSession !== null}
                  onClick={() => void revokeOthers()}
                  type="button"
                >
                  {pendingSession === "others"
                    ? "Signing out…"
                    : "Keep this browser only"}
                </button>
              </div>
            ) : (
              <button
                className="pm-sign-out-others"
                onClick={() => setConfirmOthers(true)}
                type="button"
              >
                Sign out other browsers
              </button>
            )
          ) : (
            <p className="pm-only-session">Only this browser is signed in.</p>
          )}
        </div>
      )}

      {feedback ? (
        <p
          className={`pm-security-feedback ${feedbackTone}`}
          role={feedbackTone === "error" ? "alert" : "status"}
        >
          {feedback}
        </p>
      ) : null}
    </section>
  );
}
